import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, formatDateTime } from "@/lib/api";
import { Loading, EmptyState } from "@/components/States";
import { BellOff } from "lucide-react";
import PullToRefresh from "@/components/PullToRefresh";

const PRIORITY_STYLE = {
  urgente: "border-l-red-500",
  importante: "border-l-yellow-500",
  comunidade: "border-l-primary",
  pessoal: "border-l-emerald-500",
};

function Item({ n, onOpen }) {
  const isNew = !n.read;
  return (
    <button
      onClick={() => onOpen(n)}
      data-testid="notification-item"
      className={`w-full text-left rounded-2xl border-l-2 pl-3 pr-3 py-3 flex gap-3 items-start transition-colors ${PRIORITY_STYLE[n.priority] || "border-l-white/20"} ${isNew ? "bg-white/[0.06]" : "bg-white/[0.02] hover:bg-white/[0.04]"}`}
    >
      <span className="text-lg leading-none pt-0.5">{n.icon}</span>
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${isNew ? "font-semibold" : ""}`}>{n.title}</p>
        {n.description && <p className="text-xs text-muted-foreground mt-0.5">{n.description}</p>}
        <p className="text-xs text-muted-foreground mt-0.5">{formatDateTime(n.event_at || n.created_at)}</p>
      </div>
      {isNew && <span data-testid="notif-new-dot" className="w-2 h-2 rounded-full brand-gradient mt-1.5 shrink-0" />}
    </button>
  );
}

export default function Notifications() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data, isLoading, refetch } = useQuery({ queryKey: ["notifications"], queryFn: async () => (await api.get("/notifications")).data });

  const open = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`);
      qc.invalidateQueries({ queryKey: ["notif-count"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });
    }
    if (n.destination) nav(n.destination);
  };

  if (isLoading) return <Loading />;
  const items = data?.items || [];
  const novas = items.filter((n) => !n.read);
  const anteriores = items.filter((n) => n.read);

  return (
    <PullToRefresh onRefresh={refetch}>
      <div className="space-y-5">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight">Notificações</h1>
          <p className="text-sm text-muted-foreground">Suas atenções do condomínio.</p>
        </div>

        {items.length === 0 ? (
          <EmptyState icon={BellOff} title="Nenhuma notificação" subtitle="Você está em dia com o seu condomínio." />
        ) : (
          <div className="space-y-5">
            {novas.length > 0 && (
              <section data-testid="notif-novas">
                <p className="text-xs uppercase tracking-[0.15em] text-primary font-medium mb-2">Novas</p>
                <div className="space-y-2">{novas.map((n) => <Item key={n.id} n={n} onOpen={open} />)}</div>
              </section>
            )}
            {anteriores.length > 0 && (
              <section data-testid="notif-anteriores">
                <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium mb-2">Anteriores</p>
                <div className="space-y-2">{anteriores.map((n) => <Item key={n.id} n={n} onOpen={open} />)}</div>
              </section>
            )}
          </div>
        )}
      </div>
    </PullToRefresh>
  );
}
